import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, MapPin, User, X, Check } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface ClinicianCalendarProps {
  sessions: any[];
  clients: any[];
  profile: any;
  onCreateSession: (data: any) => Promise<any>;
  onUpdateSession: (id: string, data: any) => Promise<any>;
  onDeleteSession: (id: string) => Promise<void>;
}

export function ClinicianCalendar({
  sessions,
  clients,
  profile,
  onCreateSession,
  onUpdateSession,
  onDeleteSession
}: ClinicianCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const { toast } = useToast();

  const [newSession, setNewSession] = useState({
    client_id: '',
    client_name: '',
    session_date: '',
    session_time: '09:00',
    duration_minutes: 60,
    session_type: 'individual',
    location_type: 'in_person',
    location_details: '',
    session_fee: profile?.standard_session_fee || '',
    pre_session_notes: ''
  });

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const getSessionsForDate = (date: Date) => {
    return sessions.filter(s => isSameDay(new Date(s.session_date), date));
  };

  const handleCreateSession = async () => {
    try {
      await onCreateSession({
        ...newSession,
        client_id: newSession.client_id || null,
        session_fee: newSession.session_fee ? Number(newSession.session_fee) : null
      });
      toast({ title: "Session Created", description: "Session has been added to your calendar" });
      setIsCreateOpen(false);
      setNewSession({
        client_id: '',
        client_name: '',
        session_date: '',
        session_time: '09:00',
        duration_minutes: 60,
        session_type: 'individual',
        location_type: 'in_person',
        location_details: '',
        session_fee: profile?.standard_session_fee || '',
        pre_session_notes: ''
      });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleStatusUpdate = async (sessionId: string, status: string) => {
    try {
      await onUpdateSession(sessionId, { status });
      toast({ title: "Status Updated" });
      setSelectedSession(null);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handlePaymentUpdate = async (sessionId: string, paymentStatus: string) => {
    try {
      await onUpdateSession(sessionId, { 
        payment_status: paymentStatus,
        payment_date: paymentStatus === 'paid' ? new Date().toISOString().split('T')[0] : null
      });
      toast({ title: "Payment Updated" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const openCreateForDate = (date: Date) => {
    setNewSession(prev => ({
      ...prev,
      session_date: format(date, 'yyyy-MM-dd')
    }));
    setIsCreateOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-xl">
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
                Today
              </Button>
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-2">
                    <Plus className="h-4 w-4" />
                    New Session
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Schedule New Session</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input 
                          type="date" 
                          value={newSession.session_date}
                          onChange={e => setNewSession(prev => ({ ...prev, session_date: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <Input 
                          type="time" 
                          value={newSession.session_time}
                          onChange={e => setNewSession(prev => ({ ...prev, session_time: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Client</Label>
                      <Select 
                        value={newSession.client_id}
                        onValueChange={value => {
                          if (value === 'manual') {
                            setNewSession(prev => ({ ...prev, client_id: '' }));
                          } else {
                            const client = clients.find(c => c.client_id === value);
                            setNewSession(prev => ({ 
                              ...prev, 
                              client_id: value,
                              client_name: client?.client_profile?.display_name || ''
                            }));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select client or enter manually" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Enter name manually</SelectItem>
                          {clients.filter(c => c.status === 'active').map(client => (
                            <SelectItem key={client.client_id} value={client.client_id}>
                              {client.client_profile?.display_name || 'Client'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {!newSession.client_id && (
                      <div className="space-y-2">
                        <Label>Client Name</Label>
                        <Input 
                          value={newSession.client_name}
                          onChange={e => setNewSession(prev => ({ ...prev, client_name: e.target.value }))}
                          placeholder="Enter client name"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Duration</Label>
                        <Select 
                          value={String(newSession.duration_minutes)}
                          onValueChange={value => setNewSession(prev => ({ ...prev, duration_minutes: Number(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 min</SelectItem>
                            <SelectItem value="45">45 min</SelectItem>
                            <SelectItem value="60">60 min</SelectItem>
                            <SelectItem value="90">90 min</SelectItem>
                            <SelectItem value="120">120 min</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Session Type</Label>
                        <Select 
                          value={newSession.session_type}
                          onValueChange={value => setNewSession(prev => ({ ...prev, session_type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="couples">Couples</SelectItem>
                            <SelectItem value="family">Family</SelectItem>
                            <SelectItem value="group">Group</SelectItem>
                            <SelectItem value="assessment">Assessment</SelectItem>
                            <SelectItem value="consultation">Consultation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Select 
                          value={newSession.location_type}
                          onValueChange={value => setNewSession(prev => ({ ...prev, location_type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_person">In Person</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="phone">Phone</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Fee (£)</Label>
                        <Input 
                          type="number"
                          value={newSession.session_fee}
                          onChange={e => setNewSession(prev => ({ ...prev, session_fee: e.target.value }))}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Pre-session Notes</Label>
                      <Textarea 
                        value={newSession.pre_session_notes}
                        onChange={e => setNewSession(prev => ({ ...prev, pre_session_notes: e.target.value }))}
                        placeholder="Any preparation notes..."
                        rows={2}
                      />
                    </div>

                    <Button onClick={handleCreateSession} className="w-full">
                      Schedule Session
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Week Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map(day => {
              const daySessions = getSessionsForDate(day);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  onDoubleClick={() => openCreateForDate(day)}
                  className={`
                    min-h-[80px] p-1 rounded-lg cursor-pointer transition-colors border
                    ${isCurrentMonth ? 'bg-background/50' : 'bg-background/20 opacity-50'}
                    ${isToday(day) ? 'border-primary' : 'border-border/20'}
                    ${isSelected ? 'ring-2 ring-primary' : ''}
                    hover:bg-muted/30
                  `}
                >
                  <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-primary' : 'text-foreground'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {daySessions.slice(0, 3).map(session => (
                      <div
                        key={session.id}
                        onClick={e => { e.stopPropagation(); setSelectedSession(session); }}
                        className={`
                          text-[10px] px-1 py-0.5 rounded truncate cursor-pointer
                          ${session.status === 'cancelled' ? 'bg-destructive/20 text-destructive line-through' :
                            session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            session.status === 'no_show' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-primary/20 text-primary'}
                        `}
                      >
                        {session.session_time?.slice(0, 5)} {session.client_name || 'Session'}
                      </div>
                    ))}
                    {daySessions.length > 3 && (
                      <div className="text-[10px] text-muted-foreground px-1">
                        +{daySessions.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{selectedSession.client_name || 'Client'}</span>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span>{format(new Date(selectedSession.session_date), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span>{selectedSession.session_time?.slice(0, 5)} ({selectedSession.duration_minutes} min)</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <span>{selectedSession.location_type.replace('_', ' ')}</span>
              </div>

              <div className="border-t border-border/30 pt-4">
                <Label className="mb-2 block">Session Status</Label>
                <div className="flex flex-wrap gap-2">
                  {['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show'].map(status => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedSession.status === status ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedSession.id, status)}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              {selectedSession.session_fee && (
                <div className="border-t border-border/30 pt-4">
                  <Label className="mb-2 block">Payment (£{selectedSession.session_fee})</Label>
                  <div className="flex gap-2">
                    {['pending', 'paid', 'waived'].map(status => (
                      <Button
                        key={status}
                        size="sm"
                        variant={selectedSession.payment_status === status ? 'default' : 'outline'}
                        onClick={() => handlePaymentUpdate(selectedSession.id, status)}
                      >
                        {status === 'paid' && <Check className="h-4 w-4 mr-1" />}
                        {status}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                variant="destructive" 
                size="sm"
                onClick={async () => {
                  await onDeleteSession(selectedSession.id);
                  setSelectedSession(null);
                  toast({ title: "Session deleted" });
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Delete Session
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
